import {Component, Input, OnInit, TemplateRef} from '@angular/core';

/**
 * Visual ordering for entries in the consolidated error list — lower values
 * render first. Use the named tiers so new error sources can slot themselves
 * at the right severity without re-numbering siblings. Numeric gaps (10, 20,
 * 30…) leave room for intermediate priorities if ever needed.
 */
export enum OrderErrorPriority {
  /** User has to perform an action to unblock ordering */
  ACTION_REQUIRED      = 10,
  /** Ordering is restricted under the current state. */
  BLOCKED              = 20,
  /** Event-validation restriction (limits, card rules during events). */
  EVENT_RULE           = 30,
  /** Budget will become available at a later date. */
  BUDGET_TIMING        = 40,
  /** Per-user ticket / product quota fully consumed. */
  LIMIT_REACHED        = 50,
  /** Current cost exceeds available balance. */
  BUDGET_INSUFFICIENT  = 60,
  /** Informational — overflow amounts, card charges, etc. */
  COST_INFO            = 70
}

/**
 * Single entry rendered by the consolidated error list under the order button.
 *  - `text`             : i18n key **or** raw string. The template resolves it via
 *                         `| translate`; if no matching key exists the value is
 *                         displayed as-is (safe for backend error messages).
 *  - `translationParams`: optional interpolation params forwarded to `| translate`
 *                         (e.g. `{ period: '1-5' }`).
 *  - `onClick`          : optional callback — wraps the text in a clickable link.
 *  - `template`         : optional `TemplateRef` that fully replaces the default
 *                         `<div>` rendering (used for messages that need bespoke
 *                         markup such as tooltips or icons).
 *  - `className`        : optional CSS class override for the default wrapper div.
 *  - `priority`         : optional render order (see `OrderErrorPriority`). Lower renders
 *                         first; omitted entries fall to the bottom of the list.
 */
export interface ErrorMessage {
  text?: string;
  translationParams?: Record<string, unknown>;
  onClick?: () => void;
  template?: TemplateRef<any>;
  className?: string;
  priority?: OrderErrorPriority;
}

@Component({
  selector: 'app-error-messages',
  templateUrl: './error-messages.component.html',
  styleUrls: ['./error-messages.component.scss']
})
export class ErrorMessagesComponent implements OnInit {
  @Input() errorMessages: ErrorMessage[] = [];
  @Input() extraClass = '';

  constructor() { }

  ngOnInit(): void {
  }

  readonly defaultClass = 'not-eligible-warning font-small-3 mt-2';

  getErrorClass(error: ErrorMessage): string {
    return `${error.className || this.defaultClass} ${this.extraClass || ''}`.trim();
  }
}
